import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, Loader2 } from 'lucide-react'
import PolicyGate from '../components/policy/PolicyGate'
import PolicyDocumentView from '../components/policy/PolicyDocumentView'
import { fetchPolicyMeta } from '../services/policySignService'

function grantKey(token) {
  return `policy_grant:${token}`
}

function readStoredGrant(token) {
  try {
    const raw = sessionStorage.getItem(grantKey(token))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.email && parsed?.grant) return parsed
  } catch {
    /* ignore malformed storage */
  }
  return null
}

export default function PolicySignRoutes() {
  const { token } = useParams()
  const [meta, setMeta] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ready | invalid | inactive
  const [verified, setVerified] = useState(() => readStoredGrant(token))

  useEffect(() => {
    let active = true
    setStatus('loading')
    fetchPolicyMeta(token)
      .then((data) => {
        if (!active) return
        if (!data || data.exists === false) {
          setStatus('invalid')
        } else if (!data.is_active) {
          setStatus('inactive')
        } else {
          setMeta(data)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!active) return
        setStatus('invalid')
      })
    return () => { active = false }
  }, [token])

  const handleVerified = useCallback((result) => {
    setVerified(result)
    try {
      sessionStorage.setItem(grantKey(token), JSON.stringify(result))
    } catch {
      /* storage unavailable — keep in-memory only */
    }
  }, [token])

  const handleExpire = useCallback(() => {
    setVerified(null)
    try {
      sessionStorage.removeItem(grantKey(token))
    } catch {
      /* ignore */
    }
  }, [token])

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-b from-orange-50 via-gray-50 to-gray-100 p-4">
      {status === 'loading' ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={18} className="animate-spin" aria-hidden /> Checking this link…
        </div>
      ) : null}

      {status === 'invalid' ? (
        <StatusCard
          title="This signing link is no longer valid"
          message="The link may have been regenerated or removed. Please ask your administrator for a fresh link."
        />
      ) : null}

      {status === 'inactive' ? (
        <StatusCard
          title="This document is not accepting signatures"
          message="It has been deactivated. Please check back later or contact your administrator."
        />
      ) : null}

      {status === 'ready' ? (
        verified ? (
          <PolicyDocumentView
            token={token}
            email={verified.email}
            grant={verified.grant}
            onExpire={handleExpire}
          />
        ) : (
          <PolicyGate token={token} policyName={meta?.name} onVerified={handleVerified} />
        )
      ) : null}
    </div>
  )
}

function StatusCard({ title, message }) {
  return (
    <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl ring-1 ring-gray-100">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
        <AlertTriangle size={24} aria-hidden />
      </span>
      <h1 className="mt-4 text-base font-bold text-gray-900">{title}</h1>
      <p className="mt-2 text-sm text-gray-600">{message}</p>
    </div>
  )
}
